import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { SaleInvoicePDF } from 'src/components/SaleInvoice';
import { vars } from 'src/core/themes';
import { ButtonProps } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { DownloadIcon, ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { SaleDetails } from 'src/WebApiController';

import { useSaleInvoiceCatalogData } from './LaunchDownloadSaleInvoice.hooks';
import {
  SaleInvoiceFormData,
  SaleInvoiceOptionsPopover,
} from './SaleInvoice/SaleInvoiceOptionsPopover';

export const LaunchDownloadSaleInvoice = ({
  sale,
  variant,
  iconOnlyMode,
  disabled,
}: {
  sale: SaleDetails;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { event, performer, venue, eligibleToDownload, buyer, saleListing } =
    useSaleInvoiceCatalogData(sale);

  // Download handler for the existing (non-retail) invoice
  const generateAndDownloadPDF = useCallback(
    async (formData: SaleInvoiceFormData) => {
      setIsLoading(true);
      try {
        const blob = await pdf(
          <SaleInvoicePDF
            event={event!}
            performer={performer}
            venue={venue}
            sale={sale}
            buyer={buyer}
            saleListing={saleListing}
            invoiceFormData={formData}
          />
        ).toBlob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `SaleInvoice_${sale.id}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error generating sale invoice PDF:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [buyer, event, performer, sale, saleListing, venue]
  );

  const iconFill =
    iconOnlyMode || (variant && variant !== 'regular')
      ? vars.color.textBrand
      : vars.color.textInverted;

  const buttonDisabled = disabled || isLoading || !eligibleToDownload;

  const downloadButton = (
    <ButtonWithIcon
      disabled={buttonDisabled}
      variant={variant}
      style={variant === 'textPlain' ? { width: '100%' } : undefined}
      justifyContent="space-between"
      iconOnlyMode={iconOnlyMode}
      textContentId={ContentId.PrintInvoice}
      icon={
        isLoading ? (
          <RotatingWrapper>
            <ProcessingIcon fill={iconFill} />
          </RotatingWrapper>
        ) : (
          <DownloadIcon
            withHoverEffect
            disabled={buttonDisabled}
            fill={iconFill}
            size={vars.iconSize.m}
          />
        )
      }
    />
  );

  // For non-Retail sales: show the options popover first
  return (
    <SaleInvoiceOptionsPopover
      disabled={buttonDisabled}
      sale={sale}
      onConfirm={generateAndDownloadPDF}
    >
      {downloadButton}
    </SaleInvoiceOptionsPopover>
  );
};
